import { useState, useEffect } from "react";
import { Bell, Calendar, CheckCircle, Clock, X, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Notifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // Get upcoming calendar events
      const eventsResponse = await axios.get(`${API}/calendar/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const now = new Date();
      const upcomingEvents = eventsResponse.data
        .map(event => ({
          ...event,
          start: new Date(event.start_time),
          end: new Date(event.end_time)
        }))
        .filter(event => event.start > now)
        .sort((a, b) => a.start - b.start);
      
      // Transform to notifications
      const eventNotifications = upcomingEvents.map(event => {
        const timeDiff = event.start - now;
        const hoursDiff = Math.floor(timeDiff / (1000 * 60 * 60));
        const minutesDiff = Math.floor(timeDiff / (1000 * 60));
        
        let timeText = "";
        let priority = "normal";
        
        if (minutesDiff < 60) {
          timeText = `en ${minutesDiff} minutos`;
          priority = "urgent";
        } else if (hoursDiff < 24) {
          timeText = `en ${hoursDiff}h ${minutesDiff % 60}min`;
          priority = "high";
        } else {
          const days = Math.floor(hoursDiff / 24);
          timeText = `en ${days} día${days > 1 ? 's' : ''}`;
          priority = "normal";
        }
        
        return {
          id: event.id,
          type: "calendar",
          title: event.title,
          message: `Evento programado ${timeText}`,
          time: event.start,
          priority,
          read: false,
          data: event
        };
      });
      
      setNotifications(eventNotifications);
    } catch (error) {
      console.error("Error loading notifications:", error);
      toast.error("Error al cargar notificaciones");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = (id) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === id ? { ...notif, read: true } : notif
      )
    );
  };

  const deleteNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
    toast.success("Notificación eliminada");
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
    toast.success("Todas las notificaciones marcadas como leídas");
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "bg-red-100 border-red-300 text-red-800";
      case "high":
        return "bg-orange-100 border-orange-300 text-orange-800";
      default:
        return "bg-blue-100 border-blue-300 text-blue-800";
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case "urgent":
        return <Badge variant="destructive" className="text-xs">Urgente</Badge>;
      case "high":
        return <Badge variant="warning" className="text-xs bg-orange-500">Próximo</Badge>;
      default:
        return <Badge variant="secondary" className="text-xs">Programado</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Clock className="animate-spin mx-auto mb-4" size={48} />
          <p>Cargando notificaciones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-500 rounded-lg">
            <Bell className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Notificaciones</h1>
            {unreadCount > 0 && (
              <p className="text-sm text-gray-600">
                {unreadCount} notificación{unreadCount > 1 ? 'es' : ''} sin leer
              </p>
            )}
          </div>
        </div>
        
        {notifications.length > 0 && (
          <Button
            onClick={markAllAsRead}
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
          >
            <CheckCircle className="mr-2" size={16} />
            Marcar todas como leídas
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Bell className="mx-auto text-gray-400 mb-4" size={64} />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No tienes notificaciones
              </h3>
              <p className="text-sm text-gray-600">
                Tus notificaciones de eventos y recordatorios aparecerán aquí
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <ScrollArea className="h-[calc(100vh-200px)]">
          <div className="space-y-3">
            {notifications.map((notification) => (
              <Card
                key={notification.id}
                className={`transition-all hover:shadow-md ${
                  notification.read ? 'opacity-60' : ''
                } ${getPriorityColor(notification.priority)} border-l-4`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {notification.type === "calendar" && (
                        <Calendar className="text-orange-600" size={20} />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="font-semibold text-sm sm:text-base text-gray-900 truncate">
                          {notification.title}
                        </h4>
                        {getPriorityBadge(notification.priority)}
                      </div>
                      
                      <p className="text-sm text-gray-700 mb-2">
                        {notification.message}
                      </p>
                      
                      {notification.data && (
                        <div className="text-xs text-gray-600 space-y-1">
                          <p className="flex items-center gap-1">
                            <Clock size={12} />
                            {format(notification.time, "PPp", { locale: es })}
                          </p>
                          {notification.data.location && (
                            <p className="truncate">📍 {notification.data.location}</p>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2 mt-3">
                        {!notification.read && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markAsRead(notification.id)}
                            className="text-xs h-7"
                          >
                            <CheckCircle size={12} className="mr-1" />
                            Marcar como leída
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteNotification(notification.id)}
                          className="text-xs h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={12} className="mr-1" />
                          Eliminar
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
